/**
 * UserSettingsCompliance — Bóveda Digital de "Documentos y Cumplimiento Legal"
 *
 * QUÉ HACE:
 * - Almacena de forma centralizada y segura documentos regulatorios del negocio.
 * - Soporta Carga (Data URL), Visión rápida, Descarga/Compartir, Edición y Borrado.
 * - Plantilla base precargada para Colombia, extensible y personalizable para cualquier país.
 * - Guía de seguridad de inspectores de referencia rápida.
 * - Cumple con la integridad ACID local gracias a transactionalStore.
 */

import React, { useState, useMemo } from "react";
import {
  FileText,
  Upload,
  Eye,
  Download,
  Trash2,
  Plus,
  ShieldAlert,
  Lock,
  Unlock,
  Calendar,
  Building,
  X,
  PlusCircle,
} from "lucide-react";
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";
import toast from "react-hot-toast";

interface ComplianceDocument {
  id: string;
  nombre: string;
  entidad: string;
  porque: string;
  riesgo: string;
  privacidad: "public" | "private";
  archivoUrl?: string; // Base64 data URL
  fechaVencimiento?: string;
}

const DEFAULT_DOCUMENTS: ComplianceDocument[] = [
  {
    id: "rut_nit",
    nombre: "RUT / NIT (Identificación Fiscal)",
    entidad: "DIAN (Dirección de Impuestos)",
    porque:
      "Identifica tributariamente al negocio con su actividad comercial exacta.",
    riesgo:
      "Sanciones tributarias severas o clausura del local por evasión / desactualización.",
    privacidad: "private",
  },
  {
    id: "matricula_mercantil",
    nombre: "Matrícula Mercantil",
    entidad: "Cámara de Comercio local",
    porque:
      "Acredita legalmente la existencia del establecimiento y su propiedad mercantil.",
    riesgo:
      "Sanciones pecuniarias o suspensión mercantil por operar sin registro formal.",
    privacidad: "public",
  },
  {
    id: "uso_suelos",
    nombre: "Concepto de Uso de Suelos",
    entidad: "Secretaría de Planeación Municipal",
    porque:
      "Aval de que la actividad comercial está permitida en esa zona geográfica específica.",
    riesgo: "Sellamiento inmediato del local por actividad no autorizada.",
    privacidad: "public",
  },
  {
    id: "concepto_sanitario",
    nombre: "Concepto Sanitario (Saneamiento)",
    entidad: "Secretaría de Salud / Ente Sanitario",
    porque:
      "Certifica que el local cumple las normas higiénicas y de manipulación de alimentos.",
    riesgo:
      "Decomiso de mercancías y clausura del local por riesgo biológico o sanitario.",
    privacidad: "public",
  },
  {
    id: "bomberos",
    nombre: "Certificado de Bomberos y Seguridad",
    entidad: "Cuerpo de Bomberos",
    porque:
      "Acredita extintores vigentes, rutas de evacuación y normas contra incendios.",
    riesgo:
      "Multas y sellamiento del local comercial por peligro de seguridad inminente.",
    privacidad: "public",
  },
];

export function UserSettingsCompliance() {
  const [docs, setDocs] = useState<ComplianceDocument[]>(() =>
    txRead<ComplianceDocument[]>(
      "business_compliance_documents",
      DEFAULT_DOCUMENTS,
    ),
  );
  const [filter, setFilter] = useState<"all" | "loaded" | "pending">("all");
  const [viewingDoc, setViewingDoc] = useState<ComplianceDocument | null>(null);

  // Modal para agregar documentos personalizados
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNombre, setNewNombre] = useState("");
  const [newEntidad, setNewEntidad] = useState("");
  const [newPorque, setNewPorque] = useState("");
  const [newRiesgo, setNewRiesgo] = useState("");
  const [newPrivacidad, setNewPrivacidad] = useState<"public" | "private">(
    "public",
  );
  const [newVencimiento, setNewVencimiento] = useState("");

  const saveDocs = (next: ComplianceDocument[]) => {
    txWrite("business_compliance_documents", next);
    setDocs(next);
  };

  const handleFileUpload = (
    docId: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("El tamaño del archivo supera el límite permitido de 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const next = docs.map((d) =>
        d.id === docId ? { ...d, archivoUrl: dataUrl } : d,
      );
      saveDocs(next);
      toast.success("Documento cargado correctamente en la bóveda.");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (docId: string) => {
    const next = docs.map((d) =>
      d.id === docId ? { ...d, archivoUrl: undefined } : d,
    );
    saveDocs(next);
    toast.success("Documento eliminado de la bóveda.");
  };

  const handleDownload = (doc: ComplianceDocument) => {
    if (!doc.archivoUrl) return;
    const anchor = document.createElement("a");
    anchor.href = doc.archivoUrl;
    anchor.download =
      doc.nombre.toLowerCase().replace(/\s+/g, "_") + "_comprobante";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    toast.success("Documento descargado.");
  };

  const handleCreateDocument = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNombre.trim() || !newEntidad.trim()) {
      toast.error("Por favor completa los campos obligatorios.");
      return;
    }
    const nuevo: ComplianceDocument = {
      id: "custom_" + Date.now(),
      nombre: newNombre.trim(),
      entidad: newEntidad.trim(),
      porque:
        newPorque.trim() ||
        "Requisito complementario para la operación del local.",
      riesgo: newRiesgo.trim() || "Amonestación o demoras administrativas.",
      privacidad: newPrivacidad,
      fechaVencimiento: newVencimiento || undefined,
    };
    saveDocs([...docs, nuevo]);
    setShowAddModal(false);
    setNewNombre("");
    setNewEntidad("");
    setNewPorque("");
    setNewRiesgo("");
    setNewPrivacidad("public");
    setNewVencimiento("");
    toast.success("Nuevo requisito añadido con éxito.");
  };

  const handleDeleteDocument = (docId: string) => {
    const next = docs.filter((d) => d.id !== docId);
    saveDocs(next);
    toast.success("Requisito eliminado.");
  };

  const filteredDocs = useMemo(() => {
    return docs.filter((d) => {
      if (filter === "loaded") return !!d.archivoUrl;
      if (filter === "pending") return !d.archivoUrl;
      return true;
    });
  }, [docs, filter]);

  return (
    <div className="px-4 py-4 space-y-5">
      {/* Explicación de la bóveda digital */}
      <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-2.5 dark:border-purple-900/40 dark:bg-purple-950/20">
        <h4 className="text-xs font-extrabold text-purple-900 dark:text-purple-300 flex items-center gap-1.5 uppercase">
          <Building className="w-3.5 h-3.5 shrink-0" />
          Bóveda de Cumplimiento Legal
        </h4>
        <p className="text-[10px] sm:text-xs text-purple-700 dark:text-purple-400 mt-1 leading-relaxed">
          Sube tus papeles obligatorios para tenerlos a mano en el celular o PC.
          Previene suspensiones o multas exhibiendo rápidamente cada documento
          ante una inspección de las autoridades.
        </p>
      </div>

      {/* Selector de filtros */}
      <div className="flex border-b border-gray-100 dark:border-slate-700 text-xs font-semibold gap-4 pb-0.5">
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={`pb-2 border-b-2 transition-all ${filter === "all" ? "border-primary-500 text-primary-600 font-bold" : "border-transparent text-gray-500"}`}
        >
          Todos ({docs.length})
        </button>
        <button
          type="button"
          onClick={() => setFilter("loaded")}
          className={`pb-2 border-b-2 transition-all ${filter === "loaded" ? "border-primary-500 text-primary-600 font-bold" : "border-transparent text-gray-500"}`}
        >
          Cargados ({docs.filter((d) => d.archivoUrl).length})
        </button>
        <button
          type="button"
          onClick={() => setFilter("pending")}
          className={`pb-2 border-b-2 transition-all ${filter === "pending" ? "border-primary-500 text-primary-600 font-bold" : "border-transparent text-gray-500"}`}
        >
          Pendientes ({docs.filter((d) => !d.archivoUrl).length})
        </button>
      </div>

      {/* Lista de documentos */}
      <div className="space-y-4">
        {filteredDocs.map((doc) => (
          <div
            key={doc.id}
            className={`border rounded-xl p-3 space-y-2 transition-all ${
              doc.archivoUrl
                ? "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
                : "border-dashed border-gray-300 dark:border-slate-800 bg-gray-50/40 dark:bg-slate-900/10"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    doc.archivoUrl
                      ? "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400"
                      : "bg-gray-100 dark:bg-slate-800 text-gray-400"
                  }`}
                >
                  <FileText size={16} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-800 dark:text-slate-100 leading-tight">
                    {doc.nombre}
                  </h4>
                  <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-0.5 font-medium">
                    Regulado por: {doc.entidad}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {doc.privacidad === "private" ? (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-bold text-red-600 bg-red-100/60 dark:text-red-400 dark:bg-red-950/20 px-1.5 py-0.5 rounded-full"
                    title="Documento estrictamente confidencial / Reserva tributaria"
                  >
                    <Lock size={8} /> Privado
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-bold text-teal-600 bg-teal-100/60 dark:text-teal-400 dark:bg-teal-950/20 px-1.5 py-0.5 rounded-full"
                    title="Público / Visible para inspectores o clientes"
                  >
                    <Unlock size={8} /> Público
                  </span>
                )}
              </div>
            </div>

            {/* Expansión del porqué y riesgo */}
            <div className="rounded-lg bg-gray-50 dark:bg-slate-800/40 p-2 text-[10px] sm:text-xs leading-normal space-y-1">
              <p className="text-gray-600 dark:text-slate-300">
                <span className="font-bold">¿Por qué es exigido?</span>{" "}
                {doc.porque}
              </p>
              <p className="text-red-700 dark:text-red-400/90">
                <span className="font-bold">Sanción en caso de falta:</span>{" "}
                {doc.riesgo}
              </p>
            </div>

            {/* Acciones del archivo */}
            <div className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100 dark:border-slate-800/70">
              <div>
                {doc.fechaVencimiento && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/20 px-2 py-0.5 rounded-full">
                    <Calendar size={8} /> Vence: {doc.fechaVencimiento}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                {doc.archivoUrl ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setViewingDoc(doc)}
                      className="inline-flex items-center gap-1 p-1.5 text-xs text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-950/20 rounded-md font-bold"
                      title="Mostrar a autoridad"
                    >
                      <Eye size={12} /> Ver
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(doc)}
                      className="p-1.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 rounded-md"
                      title="Descargar papel"
                    >
                      <Download size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(doc.id)}
                      className="p-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-950/25 dark:text-red-400 rounded-md"
                      title="Eliminar papel"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                ) : (
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-[11px] font-bold rounded-xl cursor-pointer shadow-sm select-none transition-all active:scale-[0.97]">
                    <Upload size={11} className="text-white" />
                    Cargar papel
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleFileUpload(doc.id, e)}
                      className="hidden"
                    />
                  </label>
                )}

                {doc.id.startsWith("custom_") && (
                  <button
                    type="button"
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-md"
                    title="Eliminar requisito personalizado"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredDocs.length === 0 && (
          <p className="text-center text-xs text-gray-400 py-6">
            No se encontraron requisitos en este filtro.
          </p>
        )}
      </div>

      {/* Botón para añadir documento personalizado */}
      <button
        type="button"
        onClick={() => setShowAddModal(true)}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-slate-700 text-xs font-bold text-gray-600 dark:text-slate-400 hover:text-primary-600 hover:border-primary-400 transition-colors"
      >
        <PlusCircle size={14} />
        Añadir otro requisito de la región
      </button>

      {/* Guía rápida de inspectores sanitarios/gubernamentales */}
      <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3 dark:border-amber-900/30 dark:bg-amber-950/10 space-y-2">
        <h4 className="text-xs font-extrabold text-amber-800 dark:text-amber-400 flex items-center gap-1.5 uppercase">
          <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
          Guía de Inspectores Gubernamentales
        </h4>
        <ul className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-400/90 list-disc list-inside space-y-1">
          <li>
            <strong>ID oficial:</strong> los funcionarios siempre deben portar
            su credencial con foto, carné QR y emitir actas.
          </li>
          <li>
            <strong>Cero cobros físicos:</strong> ningún inspector público
            recauda multas ni cuotas pecuniarias en efectivo.
          </li>
          <li>
            <strong>Acto Administrativo:</strong> las visitas siempre culminan
            con un soporte escrito autocopiante o digital para el negocio.
          </li>
        </ul>
      </div>

      {/* Modal interactivo de creación de documento personalizado */}
      {showAddModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
          <form
            onSubmit={handleCreateDocument}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col max-h-[85vh] border border-gray-100 dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 bg-primary-600 text-white flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-xs uppercase tracking-wide flex items-center gap-1.5">
                <Plus size={14} /> Añadir Requisito Regional
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 font-medium text-xs leading-normal">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Nombre del Documento *
                </label>
                <input
                  type="text"
                  required
                  value={newNombre}
                  onChange={(e) => setNewNombre(e.target.value)}
                  placeholder="Ej: Licencia de Vallas y Avisos"
                  className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Entidad de Control *
                </label>
                <input
                  type="text"
                  required
                  value={newEntidad}
                  onChange={(e) => setNewEntidad(e.target.value)}
                  placeholder="Ej: Secretaría de Medio Ambiente"
                  className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  ¿Por qué es obligatorio?
                </label>
                <textarea
                  value={newPorque}
                  onChange={(e) => setNewPorque(e.target.value)}
                  placeholder="Explica brevemente su propósito..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Multa / Sanción aplicable
                </label>
                <textarea
                  value={newRiesgo}
                  onChange={(e) => setNewRiesgo(e.target.value)}
                  placeholder="Indica el riesgo o valor de la multa..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Privacidad
                  </label>
                  <select
                    value={newPrivacidad}
                    onChange={(e) =>
                      setNewPrivacidad(e.target.value as "public" | "private")
                    }
                    className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white"
                  >
                    <option value="public">Público</option>
                    <option value="private">Privado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Vencimiento (Opcional)
                  </label>
                  <input
                    type="date"
                    value={newVencimiento}
                    onChange={(e) => setNewVencimiento(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 flex gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-2 text-xs font-bold bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-xl text-gray-700 dark:text-slate-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 py-2 text-xs font-bold bg-primary-600 hover:bg-primary-700 text-white rounded-xl"
              >
                Añadir Requisito
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal para ver el documento en pantalla completa para inspección */}
      {viewingDoc && (
        <div
          className="fixed inset-0 z-[160] bg-black bg-opacity-95 flex flex-col"
          onClick={() => setViewingDoc(null)}
        >
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between shrink-0">
            <div className="min-w-0">
              <h3 className="font-bold text-sm truncate">
                {viewingDoc.nombre}
              </h3>
              <p className="text-[10px] text-gray-400 mt-0.5">
                Ente regulador: {viewingDoc.entidad}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setViewingDoc(null)}
              className="p-2 hover:bg-slate-800 rounded-full text-white"
              aria-label="Cerrar vista completa"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
            {viewingDoc.archivoUrl?.startsWith("data:application/pdf") ? (
              <object
                data={viewingDoc.archivoUrl}
                type="application/pdf"
                className="w-full h-full max-w-2xl bg-white rounded-lg shadow-xl"
              >
                <p className="text-white text-xs text-center">
                  Este dispositivo no admite la previsualización de PDFs en
                  línea. Por favor descárgalo.
                </p>
              </object>
            ) : (
              <img
                src={viewingDoc.archivoUrl}
                alt={viewingDoc.nombre}
                className="max-w-full max-h-full object-contain rounded shadow-lg bg-white"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>

          <div className="p-4 bg-slate-900 border-t border-slate-800 flex gap-3 shrink-0 justify-center">
            <button
              type="button"
              onClick={() => handleDownload(viewingDoc)}
              className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold"
            >
              Descargar papel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
